/**
 * CARGO CLIMB — cartridge palette.
 *
 * Industrial dockyard color script: rust orange, weathered steel, hazard
 * yellow. The cartridge renders a centered portrait playfield inside the
 * landscape canvas (per the cabinet's landscape-only rule), so the
 * gutter chrome and the playfield share the same palette to read as one
 * continuous scene — a dock seen through an oil-tinted CRT, not "game +
 * black bars".
 */

// Background — deep night-dock grime, slightly warmer than pure black so
// the rust + hazard accents sing against it without looking cold.
export const FIELD_BG = '#1a0e08';

// Side-gutter wash — a touch lighter than FIELD_BG so the playfield
// reads as a slight inset / "lit zone" without a hard border.
export const GUTTER_BG = '#221208';

// Scaffolding — weathered steel girders. Two tones for top/edge so the
// platforms have a sense of cylindrical depth.
export const SCAFFOLD = '#7c8089';
export const SCAFFOLD_DARK = '#3b3e44';

// Ladders — hazard yellow, the universal arcade "you can climb this".
export const LADDER = '#ffb000';
export const LADDER_RUNG = '#d48400';

// Player — dock-worker jumpsuit. Cyan-tinted so the player pops against
// the warm-leaning industrial wash.
export const PLAYER_BODY = '#3fb8e6';
export const PLAYER_OUTLINE = '#0c4e6a';
export const PLAYER_HEAD = '#ffdba8'; // skin tone

// Crates — rust orange for the standard square. Long crates are a
// slightly cooler tone so they read as a different object class at a
// glance. L-shape crates use the brightest hazard accent because they
// behave the least predictably (high attention reward).
export const CRATE_SQUARE = '#c8552c';
export const CRATE_SQUARE_DARK = '#7a2f15';
export const CRATE_LONG = '#a06030';
export const CRATE_LONG_DARK = '#5a3414';
export const CRATE_L = '#ff8c1a';
export const CRATE_L_DARK = '#a04a08';
// Crate-carry highlight — when the player rides a crate down, the crate
// is outlined in this color so the player can spot which crate is "theirs".
export const CRATE_CARRIED_OUTLINE = '#fff060';

// Crane silhouette — top of the playfield, mechanical antagonist.
export const CRANE_BODY = '#5a3018';
export const CRANE_CABLE = '#2a2218';
export const CRANE_LIGHT_ON = '#ffea00';
export const CRANE_LIGHT_OFF = '#5a4a08';

// HUD typography.
export const HUD_LABEL = '#7c8089'; // steel grey
export const HUD_SCORE = '#ffea00'; // hazard yellow score numerals
export const HUD_LIVES = '#3fb8e6'; // player-color life icons
export const HUD_LEVEL = '#c8552c'; // rust orange for level indicator
export const HUD_ALERT = '#ff3030'; // red for crane-active warning

// Effects.
export const FLASH_HIT = '#ffffff';
export const PLAYER_LAND_DUST = 'rgba(124, 128, 137, 0.5)';

/** Tokens the cabinet samples for marquee tinting while this cartridge
 *  is loaded. First entry is `paletteHint[0]`, used everywhere the cabinet
 *  needs a single "this is the cartridge's color" pick. */
export const PALETTE_HINT: readonly string[] = [
  CRATE_L, // hazard orange — the cartridge's hero color
  PLAYER_BODY,
  LADDER,
  SCAFFOLD,
];
